/**
 * Release 3 EXP-1 (lane C) — pure helpers for export transforms: form state ↔ `ImageTransform`, output file names,
 * human labels. Mirrors the server rules in `@lumen/shared` release3.ts (`fit` needs its size key; `quality` only on
 * lossy formats; `format: original` keeps the source extension).
 */
import type { ImageTransform } from '@/lib/api/queries';

export type ExportFormat = ImageTransform['format'];
/** A preset as the menus need it (the public resolve types `transform` loosely). */
export type PresetLike = { id: string; name: string; transform: ImageTransform | Record<string, unknown> };
export type ExportFit = NonNullable<ImageTransform['fit']>;

export const FORMAT_LABELS: Record<ExportFormat, string> = { original: 'Original format', jpeg: 'JPEG', png: 'PNG', webp: 'WebP', avif: 'AVIF' };
export const FORMAT_EXT: Record<Exclude<ExportFormat, 'original'>, string> = { jpeg: 'jpg', png: 'png', webp: 'webp', avif: 'avif' };
export const FIT_LABELS: Record<'none' | ExportFit, string> = { none: 'Keep dimensions', scale: 'Scale', long_edge: 'Long edge', box: 'Fit in box' };
export const SCALES = [1, 0.5, 0.25] as const;
export const MAX_EDGE = 8192;
export const MIN_EDGE = 16;

/** Editable form state (strings for the number inputs so partial typing is allowed). */
export type TransformForm = {
  format: ExportFormat;
  fit: 'none' | ExportFit;
  scale: (typeof SCALES)[number];
  long_edge: string;
  width: string;
  height: string;
  quality: string;
  strip_metadata: boolean;
  suffix: string;
};

export const DEFAULT_FORM: TransformForm = { format: 'jpeg', fit: 'long_edge', scale: 0.5, long_edge: '2048', width: '', height: '', quality: '', strip_metadata: true, suffix: '' };

export function formFromTransform(t: ImageTransform | null | undefined): TransformForm {
  if (!t) return DEFAULT_FORM;
  return {
    format: t.format,
    fit: t.fit ?? 'none',
    scale: (t.scale as TransformForm['scale']) ?? 0.5,
    long_edge: t.long_edge != null ? String(t.long_edge) : '',
    width: t.width != null ? String(t.width) : '',
    height: t.height != null ? String(t.height) : '',
    quality: t.quality != null ? String(t.quality) : '',
    strip_metadata: t.strip_metadata !== false,
    suffix: t.suffix ?? '',
  };
}

const int = (s: string): number | null => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : null);
const edgeOk = (n: number | null) => n != null && n >= MIN_EDGE && n <= MAX_EDGE;

/** Validate the form; returns the transform or the first problem (a message to show inline). */
export function transformFromForm(f: TransformForm): { ok: true; transform: ImageTransform } | { ok: false; error: string } {
  const t: ImageTransform = { format: f.format, strip_metadata: f.strip_metadata };
  if (f.fit === 'scale') {
    t.fit = 'scale';
    t.scale = f.scale;
  } else if (f.fit === 'long_edge') {
    const n = int(f.long_edge);
    if (!edgeOk(n)) return { ok: false, error: `Long edge must be a whole number from ${MIN_EDGE} to ${MAX_EDGE} px` };
    t.fit = 'long_edge';
    t.long_edge = n as number;
  } else if (f.fit === 'box') {
    const w = f.width.trim() ? int(f.width) : undefined;
    const h = f.height.trim() ? int(f.height) : undefined;
    if (w === undefined && h === undefined) return { ok: false, error: 'Fit in box needs a width, a height or both' };
    if ((w !== undefined && !edgeOk(w)) || (h !== undefined && !edgeOk(h))) return { ok: false, error: `Box edges must be whole numbers from ${MIN_EDGE} to ${MAX_EDGE} px` };
    t.fit = 'box';
    if (w !== undefined) t.width = w as number;
    if (h !== undefined) t.height = h as number;
  }
  if (f.quality.trim() && f.format !== 'png' && f.format !== 'original') {
    const q = int(f.quality);
    if (q == null || q < 1 || q > 100) return { ok: false, error: 'Quality must be 1–100' };
    t.quality = q;
  }
  const suffix = f.suffix.trim();
  if (suffix) {
    if (!/^[A-Za-z0-9._-]{1,24}$/.test(suffix)) return { ok: false, error: 'Suffix: up to 24 letters, digits, dots, dashes or underscores' };
    t.suffix = suffix;
  }
  if (t.format === 'original' && t.fit === undefined) return { ok: false, error: 'Choose a format or a size — otherwise this is the original file' };
  return { ok: true, transform: t };
}

/** Output name: original stem + suffix + the new extension (`original` keeps the source extension). */
export function exportFilename(originalFilename: string, t: ImageTransform): string {
  const dot = originalFilename.lastIndexOf('.');
  const stem = dot > 0 ? originalFilename.slice(0, dot) : originalFilename;
  const srcExt = dot > 0 ? originalFilename.slice(dot + 1) : '';
  const ext = t.format === 'original' ? srcExt : FORMAT_EXT[t.format];
  return `${stem}${t.suffix ?? ''}${ext ? `.${ext}` : ''}`;
}

/** One-line description, e.g. "WebP · long edge 2048 px · q 80". */
export function describeTransform(t: ImageTransform): string {
  const parts = [FORMAT_LABELS[t.format] ?? t.format];
  if (t.fit === 'scale') parts.push(`${Math.round((t.scale ?? 1) * 100)} %`);
  else if (t.fit === 'long_edge') parts.push(`long edge ${t.long_edge} px`);
  else if (t.fit === 'box') parts.push(`fit ${t.width ?? '…'} × ${t.height ?? '…'} px`);
  if (t.quality != null && t.format !== 'png' && t.format !== 'original') parts.push(`q ${t.quality}`);
  if (t.strip_metadata === false) parts.push('keeps metadata');
  return parts.join(' · ');
}
